(function () {
  'use strict';

  var MEDIAPIPE_VERSION = '0.10.14';
  var MODEL_URL =
    'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';

  // Average ratio between a glasses frame's temple-to-temple width and a
  // person's inter-pupillary distance. Real per-frame widths aren't stored in
  // the catalog (only photos), so this is an approximation — good enough for
  // a visual try-on, not a precision fit measurement.
  var FRAME_TO_EYE_DISTANCE_RATIO = 2.2;

  var faceLandmarkerPromise = null;
  var rafId = null;
  var stream = null;
  var currentModal = null;

  // Shopify's asset pipeline runs this file through a bundler (esbuild)
  // before serving it, which statically rewrites any literal `import(...)`
  // call into a `require(...)` call wrapped in a Promise — a transform
  // meant for bundling local modules, but there is no `require` at runtime
  // in the browser, so it threw "require is not defined". Hiding the
  // dynamic import inside a `new Function` body keeps the literal text
  // "import(" out of this source file, so the bundler's static scan can't
  // find and rewrite it, while the browser still evaluates a genuine native
  // dynamic import at runtime.
  var dynamicImport = new Function('specifier', 'return import(specifier);');

  function createLandmarker(vision, filesetResolver, delegate) {
    return vision.FaceLandmarker.createFromOptions(filesetResolver, {
      baseOptions: { modelAssetPath: MODEL_URL, delegate: delegate },
      runningMode: 'VIDEO',
      numFaces: 1,
      outputFaceBlendshapes: false,
      outputFacialTransformationMatrixes: false
    });
  }

  function errMsg(err) {
    return err && (err.message || err.name) ? err.message || err.name : String(err);
  }

  function loadFaceLandmarker() {
    if (faceLandmarkerPromise) return faceLandmarkerPromise;
    // Each stage is labeled with which step failed (import / wasm load /
    // model init) — a plain passthrough error here made every failure show
    // the same generic message, with no way to tell them apart without
    // opening devtools.
    //
    // The import path names the exact file (vision_bundle.mjs) rather than
    // the bare package root, so resolution isn't ambiguous between the
    // package's ESM and CommonJS builds.
    faceLandmarkerPromise = dynamicImport(
      'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@' + MEDIAPIPE_VERSION + '/vision_bundle.mjs'
    )
      .catch(function (err) {
        throw new Error('import da biblioteca: ' + errMsg(err));
      })
      .then(function (vision) {
        return vision.FilesetResolver.forVisionTasks(
          'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@' + MEDIAPIPE_VERSION + '/wasm'
        )
          .catch(function (err) {
            throw new Error('carregamento do WASM: ' + errMsg(err));
          })
          .then(function (filesetResolver) {
            // The GPU delegate isn't supported on every device (notably many
            // iOS/Safari and older Android browsers) — mediapipe throws there
            // instead of degrading gracefully, so fall back to CPU rather
            // than failing the whole feature.
            return createLandmarker(vision, filesetResolver, 'GPU').catch(function (gpuErr) {
              console.warn('[tryon] delegate GPU falhou, tentando CPU', gpuErr);
              return createLandmarker(vision, filesetResolver, 'CPU').catch(function (cpuErr) {
                throw new Error(
                  'inicialização do modelo (GPU: ' + errMsg(gpuErr) + ' | CPU: ' + errMsg(cpuErr) + ')'
                );
              });
            });
          });
      });
    // Don't cache a rejected promise — a transient network/model failure
    // shouldn't permanently break the feature for the rest of the session;
    // reopening the modal should retry from scratch.
    faceLandmarkerPromise.catch(function () {
      faceLandmarkerPromise = null;
    });
    return faceLandmarkerPromise;
  }

  function setStatus(modal, text, state) {
    var el = modal.querySelector('[data-tryon-status]');
    if (!el) return;
    el.textContent = text || '';
    if (state) {
      el.setAttribute('data-state', state);
    } else {
      el.removeAttribute('data-state');
    }
  }

  function openModal(modal) {
    currentModal = modal;
    modal.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    setStatus(modal, 'Iniciando câmera…');

    var video = modal.querySelector('.tryon-video');
    var glasses = modal.querySelector('.tryon-glasses-img');

    // Camera access and model loading are handled as two separate steps,
    // each with its own error handling — this used to be a single
    // Promise.all(), which meant a model-loading failure (e.g. unsupported
    // GPU delegate) surfaced as a misleading "can't access camera" message
    // even when the camera itself worked fine.
    navigator.mediaDevices
      .getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false
      })
      .then(function (mediaStream) {
        if (currentModal !== modal) {
          mediaStream.getTracks().forEach(function (track) {
            track.stop();
          });
          return;
        }

        stream = mediaStream;
        video.srcObject = stream;
        setStatus(modal, 'Carregando rastreamento facial…');

        return video
          .play()
          .then(function () {
            return loadFaceLandmarker();
          })
          .then(function (faceLandmarker) {
            if (currentModal !== modal) return;
            setStatus(modal, '', 'ready');
            startLoop(modal, video, glasses, faceLandmarker);
          })
          .catch(function (err) {
            if (currentModal !== modal) return;
            console.error('[tryon] falha ao carregar o rastreamento facial', err);
            // Surfaced directly in the UI (not just the console) so a
            // non-technical user hitting this can screenshot the real cause
            // instead of a generic message that hides it.
            setStatus(
              modal,
              'Não foi possível carregar o rastreamento facial: ' + errMsg(err),
              'error'
            );
          });
      })
      .catch(function (err) {
        if (currentModal !== modal) return;
        console.error('[tryon] falha ao acessar a câmera', err);
        var message = 'Não foi possível acessar a câmera.';
        if (err && err.name === 'NotAllowedError') {
          message = 'Permissão de câmera negada. Habilite o acesso à câmera no navegador e tente de novo.';
        } else if (err && err.name === 'NotFoundError') {
          message = 'Nenhuma câmera encontrada neste dispositivo.';
        } else if (err && err.name === 'NotReadableError') {
          message = 'A câmera está sendo usada por outro aplicativo. Feche-o e tente novamente.';
        }
        setStatus(modal, message, 'error');
      });
  }

  function startLoop(modal, video, glasses, faceLandmarker) {
    var bboxWFrac = parseFloat(modal.dataset.bboxWFrac) || 0.9;
    var bboxCxFrac = parseFloat(modal.dataset.bboxCxFrac) || 0.5;
    var bboxCyFrac = parseFloat(modal.dataset.bboxCyFrac) || 0.45;
    var lastNoFaceNotice = 0;

    // A single failed detection (e.g. a transient WASM/GPU-delegate hiccup
    // on one frame) used to throw out of this function entirely, which
    // aborted the requestAnimationFrame chain for good — nothing after that
    // point calls requestAnimationFrame(frame) again, so tracking silently
    // freezes on whatever position the last successful frame computed,
    // looking like a static image glued near the eye instead of live
    // tracking. Wrapping the per-frame work keeps the loop alive no matter
    // what a single frame does.
    function frame() {
      try {
        tick();
      } catch (err) {
        console.error('[tryon] falha ao processar frame de rastreamento', err);
      }
      rafId = requestAnimationFrame(frame);
    }

    function tick() {
      if (currentModal !== modal || video.readyState < 2 || !glasses.naturalWidth) {
        return;
      }

      var result = faceLandmarker.detectForVideo(video, performance.now());
      var landmarks = result && result.faceLandmarks && result.faceLandmarks[0];

      if (!landmarks) {
        glasses.style.opacity = '0';
        var now = performance.now();
        if (now - lastNoFaceNotice > 1500) {
          setStatus(modal, 'Centralize seu rosto na câmera', 'ready');
          lastNoFaceNotice = now;
        }
        return;
      }

      setStatus(modal, '', 'ready');
      glasses.style.opacity = '1';

      var vw = video.videoWidth;
      var vh = video.videoHeight;

      // The video's on-screen box and crop were previously left to CSS
      // `object-fit: cover`, with this code separately re-deriving the same
      // cover-fit math to place the glasses — two independent
      // implementations of the same mapping, which only need to disagree by
      // a little (e.g. how `cover` interacts with the `scaleX(-1)` mirror
      // transform on the same element) to throw the glasses off. Sizing and
      // positioning the video element from these exact numbers instead
      // means both the video and the glasses are driven by the one
      // computation, so they can't drift apart.
      var stageRectForVideo = modal.querySelector('.tryon-modal__stage').getBoundingClientRect();
      var videoCoverScale = Math.max(stageRectForVideo.width / vw, stageRectForVideo.height / vh);
      var videoRenderedWidth = vw * videoCoverScale;
      var videoRenderedHeight = vh * videoCoverScale;
      var videoCropOffsetX = (stageRectForVideo.width - videoRenderedWidth) / 2;
      var videoCropOffsetY = (stageRectForVideo.height - videoRenderedHeight) / 2;
      video.style.width = videoRenderedWidth + 'px';
      video.style.height = videoRenderedHeight + 'px';
      video.style.left = videoCropOffsetX + 'px';
      video.style.top = videoCropOffsetY + 'px';

      // Outer eye corners (canonical MediaPipe face mesh indices).
      var eyeA = landmarks[33];
      var eyeB = landmarks[263];

      var ax = eyeA.x * vw;
      var ay = eyeA.y * vh;
      var bx = eyeB.x * vw;
      var by = eyeB.y * vh;

      var eyeDistancePx = Math.hypot(bx - ax, by - ay);
      var midX = (ax + bx) / 2;
      var midY = (ay + by) / 2;

      // Video is mirrored via CSS (scaleX(-1)) for a natural selfie view,
      // so mirror the x coordinate to match what's on screen.
      var mirroredMidX = vw - midX;

      // Reuses the exact numbers just applied to the video element itself
      // (above), instead of a second independent cover-fit calculation, so
      // the glasses can't end up mapped against a video box that isn't the
      // one actually on screen.
      var screenX = videoCropOffsetX + mirroredMidX * videoCoverScale;
      var screenY = videoCropOffsetY + midY * videoCoverScale;

      var glassesFullWidthPx = (eyeDistancePx * FRAME_TO_EYE_DISTANCE_RATIO) / bboxWFrac;
      var displayScale = (glassesFullWidthPx * videoCoverScale) / glasses.naturalWidth;

      var roll = Math.atan2(by - ay, bx - ax);
      // Mirrored view: flip roll sign so tilt matches what's on screen.
      var rollDeg = -(roll * 180) / Math.PI;

      // Rough, conservative yaw estimate from eye depth difference — clamped
      // so a wrong sign or noisy reading never breaks the visual, only
      // slightly under/over-tilts it.
      var dz = (eyeB.z - eyeA.z) * vw;
      var yawDeg = Math.max(-12, Math.min(12, dz * 0.15));

      // Shift from the full image's geometric center to the visible frame's
      // bbox center (there's transparent margin around the cutout), in
      // on-screen pixels, so the frame itself — not the padded canvas —
      // lands on the eye midpoint.
      var offsetXPx = (bboxCxFrac - 0.5) * glasses.naturalWidth * displayScale;
      var offsetYPx = (bboxCyFrac - 0.5) * glasses.naturalHeight * displayScale;

      glasses.style.left = (screenX - offsetXPx) + 'px';
      glasses.style.top = (screenY - offsetYPx) + 'px';
      glasses.style.transform =
        'translate(-50%, -50%) ' +
        'perspective(700px) ' +
        'rotateZ(' + rollDeg + 'deg) ' +
        'rotateY(' + yawDeg + 'deg) ' +
        'scale(' + displayScale + ')';
    }

    rafId = requestAnimationFrame(frame);
  }

  function closeModal(modal) {
    if (currentModal === modal) currentModal = null;
    if (rafId) cancelAnimationFrame(rafId);
    rafId = null;
    if (stream) {
      stream.getTracks().forEach(function (track) {
        track.stop();
      });
      stream = null;
    }
    var video = modal.querySelector('.tryon-video');
    video.srcObject = null;
    modal.hidden = true;
    document.documentElement.style.overflow = '';
  }

  function init() {
    var openButtons = document.querySelectorAll('[data-tryon-open]');
    openButtons.forEach(function (button) {
      var wrap = button.closest('.tryon-trigger-wrap');
      var modal = wrap && wrap.nextElementSibling;
      if (!modal || !modal.hasAttribute('data-tryon-modal')) return;
      button.addEventListener('click', function () {
        openModal(modal);
      });
      modal.querySelectorAll('[data-tryon-close]').forEach(function (closeEl) {
        closeEl.addEventListener('click', function () {
          closeModal(modal);
        });
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !modal.hidden) closeModal(modal);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
